// Oregon State University: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - OSU's Common Data Set 2025-2026 (CDS-2025-26_1.pdf), the file
//    Institutional Research links from its Common Data Set page: C1
//    applicants, admits and enrollees for Fall 2025 with the residency table,
//    C8 test policy for Fall 2027, C21/C22 early plans, G1/G5 2026-2027 costs
//    (the first-year tuition column; OSU's continuing undergraduates pay a
//    little less).
//  - admissions.oregonstate.edu: Undergraduate Admission Deadlines (the Fall
//    2027 dates, given with their years), Admission Requirements (holistic
//    review, the engineering math requirement, early action terms) and Test
//    Optional Admissions. The Admission Requirements page still prints the
//    previous cycle's dates (November 3, February 2, February 13); the page
//    uses the deadlines page's Fall 2027 dates. OSU publishes no decision date
//    for the priority round.
//  - College Scorecard, school id 209542 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Oregon State Common Data Set 2025-2026',
        url: 'https://institutionalresearch.oregonstate.edu/common-data-set',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Oregon State Admissions, Undergraduate Admission Deadlines',
        url: 'https://admissions.oregonstate.edu/undergraduate-admission-deadlines',
        read: '2026-10-01',
    },
    requirements: {
        label: 'Oregon State Admissions, Admission Requirements',
        url: 'https://admissions.oregonstate.edu/admission-requirements',
        read: '2026-10-01',
    },
    testOptional: {
        label: 'Oregon State Admissions, Test Optional Admissions',
        url: 'https://admissions.oregonstate.edu/test-optional-admissions',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?209542-Oregon-State-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'oregon-state-university',
    kbId: 'oregon_state_university',
    name: 'Oregon State University',
    shortName: 'Oregon State',
    location: 'Corvallis, OR',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (OSU brand guide, colors); decoration only.
    branding: {
        primary: '#D73F09',
        secondary: '#000000',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/oregon_state_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 29362,
        admitted: 23172,
        enrolled: 4452,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 8610, admitted: 7199 },
            { group: 'Out-of-state', applied: 20412, admitted: 15849 },
            { group: 'International', applied: 340, admitted: 124 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Oregon State\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major. OSU says your intended major is for advising and is not part of the admission decision.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'Oregon State has no early decision plan. Its early action plan is not binding and not restrictive, and the Common Data Set does not report how many applied or were admitted early, so it gives no early acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Your intended major is for advising only and is not considered in the admission decision. Two areas work differently:',
        rows: [
            { school: 'College of Engineering', requirement: 'To be admitted directly into an engineering major, you must have taken pre-calculus or calculus in high school. If you are not ready for calculus in your first term, you may still be admitted to OSU, but you must get ready for calculus before you are admitted to the engineering major.', source: 'requirements' },
            { school: 'Honors College', requirement: 'Admission is competitive and separate: it looks at your cumulative unweighted high school GPA and your answer to the essay question. SAT or ACT scores are optional but can be used to apply if you do not meet the minimum GPA.', source: 'testOptional' },
        ],
        source: 'testOptional',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'February 1, 2027' },
        rows: [
            { plan: 'Early Action', deadline: 'November 2, 2026', decision: 'Mid-December', binding: false },
            { plan: 'Priority Application', deadline: 'February 1, 2027', decision: null, binding: false },
        ],
        note: 'These are the deadlines for the Corvallis campus. If you apply by February 1, your transcripts, other required documents and any test scores you want considered are due February 12, 2027. After February 1, admission and scholarships are offered as space and funds allow. Early action applicants with a complete file hear by mid-December, though some are asked for mid-year transcripts first. OSU does not publish a decision date for the priority round.',
        source: 'deadlines',
        binding: 'Early Action is not binding: it does not oblige you to choose Oregon State if admitted. Early action and priority applicants are reviewed by the same criteria, with no advantage for either deadline, and an early action file not complete by the deadline is considered in the priority round.',
        bindingSource: 'requirements',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Oregon State is test-optional for first-year applicants. You say when you apply whether you will send a score. If you apply test-optional, any score already sent is removed from review. Scores you do send are never the sole or primary reason for a decision. OSU uses the best composite from a single test date and does not combine sections across dates.',
        source: 'testOptional',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [13440, 40155] },
            { item: 'Required fees', amounts: [2592, 2592] },
            { item: 'Housing (on campus)', amounts: [13311, 13311] },
            { item: 'Food (on-campus meal plan)', amounts: [5700, 5700] },
            { item: 'Books and supplies', amounts: [600, 600] },
            { item: 'Transportation', amounts: [930, 930] },
            { item: 'Other expenses', amounts: [2958, 2958] },
        ],
        totals: [39531, 66246],
        note: 'For a full-time first-year student living on campus, the full academic year. OSU reports that 50.5% of full-time undergraduates pay more than this tuition and fees, because charges vary by program.',
        source: 'cds',
        where: 'sections G1, G4 and G5',
        netPrice: {
            average: 21504,
            byIncome: [
                { income: '$0 to $30,000', amount: 16485 },
                { income: '$30,001 to $48,000', amount: 15853 },
                { income: '$48,001 to $75,000', amount: 16651 },
                { income: '$75,001 to $110,000', amount: 21090 },
                { income: 'Over $110,000', amount: 29717 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Oregon State\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
